import { useEffect, useState } from "react";
import { Check, MailCheck, Phone, RotateCcw } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDateTimeMtl } from "../../lib/dateMtl";
import ExpandableText from "./ExpandableText";

// Carte « Demandes d'appel » : visiteurs qui ont demandé à être rappelés (bouton
// « Demander un appel ») — à cocher « fait » au fil des rappels.
// Affichée UNIQUEMENT en popup depuis l'onglet « Ventes & Soumissions »
// (demande du propriétaire : un bouton dans l'en-tête, pas la carte partout).
export default function CallRequestsCard() {
  const [items, setItems] = useState([]);
  const [showDone, setShowDone] = useState(false);

  const load = () => api.get("/admin/call-requests")
    .then(({ data }) => setItems(data.requests || []))
    .catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (r, done) => {
    try {
      await api.patch(`/admin/call-requests/${r.id}`, { done });
      setItems((xs) => xs.map((x) => (x.id === r.id ? { ...x, done } : x)));
    } catch { /* silencieux : la liste se rechargera */ }
  };

  const pending = items.filter((r) => !r.done);
  const done = items.filter((r) => r.done);

  const Row = ({ r, isDone }) => {
    const details = [r.organization, r.postal_code || r.city, r.best_time].filter(Boolean).join(" · ");
    return (
      <li data-testid={`call-request-${r.id}`}
        className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-xs ${isDone ? "border-ivory/5 opacity-50" : "border-amber-500/25"}`}>
        <button
          onClick={() => toggle(r, !isDone)}
          data-testid={`call-done-${r.id}`}
          title={isDone ? "Remettre en attente" : "Marquer « rappelé »"}
          className={`mt-0.5 shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
            isDone ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400" : "border-gold/50 text-transparent hover:border-gold"}`}>
          <Check size={13} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-ivory font-semibold">{r.name}</p>
          {details && <ExpandableText text={details} testId={`call-details-${r.id}`} className="block text-ivory/50" />}
          {r.message && <ExpandableText text={r.message} testId={`call-message-${r.id}`} className="block text-ivory/45 italic" />}
          <a href={`tel:+${(r.phone || "").replace(/[^0-9]/g, "")}`} data-testid={`call-phone-${r.id}`}
            className="inline-flex items-center gap-1 text-amber-300 hover:text-gold font-semibold mt-0.5 transition-colors">
            <Phone size={11} /> {r.phone}
          </a>
          {r.followup_sent && (
            <span data-testid={`call-followup-${r.id}`}
              className="flex items-center gap-1 text-gold/70 mt-1"
              title="Courriel de relance automatique envoyé au client après 48 h sans réponse">
              <MailCheck size={11} /> Relance auto 48 h envoyée le {fmtDateTimeMtl(r.followup_sent_at)}
            </span>
          )}
        </div>
        <span className="text-ivory/40 shrink-0">{(r.created_at || "").slice(5, 16).replace("T", " ")}</span>
        {isDone && (
          <button onClick={() => toggle(r, false)} data-testid={`call-undo-${r.id}`}
            title="Remettre en attente"
            className="shrink-0 text-ivory/40 hover:text-gold transition-colors">
            <RotateCcw size={13} />
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="bg-surface border border-gold/15 rounded-xl p-6 mb-8" data-testid="call-requests-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-widest text-gold inline-flex items-center gap-2">
          <Phone size={12} /> Demandes d'appel
          {pending.length > 0 && (
            <span className="bg-amber-500/20 text-amber-300 rounded-full px-2 py-0.5 normal-case tracking-normal"
              data-testid="call-requests-pending-count">{pending.length} en attente</span>
          )}
        </h3>
        {done.length > 0 && (
          <button onClick={() => setShowDone((v) => !v)} data-testid="call-requests-toggle-done"
            className="text-[11px] text-ivory/50 hover:text-gold underline underline-offset-2 transition-colors">
            {showDone ? "Masquer" : `Voir les rappelés (${done.length})`}
          </button>
        )}
      </div>
      {pending.length === 0 ? (
        <p className="text-xs text-ivory/50" data-testid="call-requests-empty">
          Aucune demande en attente — le bouton « Demander un appel » du site fait tout arriver ici et à info@trophee.ca.
        </p>
      ) : (
        <ul className="space-y-2">{pending.map((r) => <Row key={r.id} r={r} isDone={false} />)}</ul>
      )}
      {showDone && done.length > 0 && (
        <ul className="space-y-2 mt-4 pt-4 border-t border-ivory/10">
          {done.map((r) => <Row key={r.id} r={r} isDone={true} />)}
        </ul>
      )}
    </div>
  );
}
